/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import Image from 'next/legacy/image';
import styled, { css } from 'styled-components';
import { Popover } from 'antd';
import i18next from 'i18next';

import AgentWarrantyPopoverContent from '@design-system/components/BrowsingCard/AgentWarrantyPopoverContent';
import useGTMEvent from '@source/hooks/useGTMEvent';

interface IFeatureFlag {
  showBadge?: boolean;
  label?: string;
  color?: string;
  icon?: string;
  name?: string;
}

interface IFeatureBadge {
  featureFlags?: Array<IFeatureFlag>;
  carTitle?: any;
  href?: string;
  agentWarranty?: {
    expired_date: string;
    mileage: number;
    warranty_by: string;
  };
  insideBadges: {
    id?: string;
    title?: any;
    description?: any;
    icon?: string;
  }[];
  v2?: boolean;
}

const StyledFeatureBadgeWrapper = styled.div`
  min-height: 22px;
  margin-top: 12px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
`;

const StyledFeatureFlags = styled.span<{ v2?: boolean }>`
  &:last-child {
    margin-right: 0px;
  }

  span {
    margin-right: 2px !important;
  }

  .feature-flag {
    font-size: 9px;
    ${(props) => props.theme.typo.familyGoogle.medium};
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 140%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 4.5px;
    border-radius: 4px;
    border: 1px solid ${(props) => props.color};
    color: ${(props) => props.color};
  }

  .agent-warranty {
    ${(props) => props.theme.typo.familyGoogle.regular};
    font-size: 12px;
    line-height: 20px;
    color: ${(props) => props.theme.color.primaryBase};
    display: flex;

    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 4px;

    ${(props) =>
      !props.v2 &&
      css`
        background: #ffede5;
        padding: 4px;
      `}
    span {
      flex-shrink: 0;
      max-width: 14px;
    }

    img {
      max-width: 14px;
    }
  }
`;

const FeatureBadge: React.FC<IFeatureBadge> = ({
  featureFlags = [],
  carTitle,
  href,
  agentWarranty,
  insideBadges = [],
  v2,
}) => {
  const { pushGTMEvent } = useGTMEvent();

  const lang = i18next.language;

  const handleClickWA = () => {
    pushGTMEvent({
      event: 'click_whatsapp_aw',
    });
  };

  const renderAgentWarrantyTag = (icon: any, label: string) => (
    <Popover
      placement="bottom"
      overlayClassName="agent-warranty-popover"
      content={
        <AgentWarrantyPopoverContent
          carTitle={carTitle}
          href={href}
          agentWarranty={agentWarranty}
          onClickWA={handleClickWA}
        />
      }
      zIndex={900}
      trigger={['hover', 'click']}
    >
      <StyledFeatureFlags v2={v2}>
        <div className="agent-warranty">
          {icon && <Image src={icon} alt="label" width="14" height="14" />}
          {label}
        </div>
      </StyledFeatureFlags>
    </Popover>
  );

  const hasAgentWarranty = featureFlags.some((flag) => flag.name === 'agent_warranty' && flag.showBadge);
  const unShowedTags = insideBadges.length - (hasAgentWarranty ? 1 : 2);
  const hasBadges = featureFlags.some((flag) => flag.showBadge) || insideBadges.length > 0;

  if (!hasBadges) {
    return null;
  }

  return (
    <StyledFeatureBadgeWrapper>
      {featureFlags?.map((flag: IFeatureFlag, index) => {
        const { showBadge, label, color, icon, name } = flag;
        if (showBadge) {
          return name !== 'agent_warranty' ? (
            // eslint-disable-next-line react/no-array-index-key
            <StyledFeatureFlags color={color} v2={v2} key={`${label}-${index}`}>
              <div className="feature-flag">
                {icon && <Image src={icon} alt="label" width="11" height="11" />}
                {label}
              </div>
            </StyledFeatureFlags>
          ) : (
            <div
              key={`${label}-${index}`}
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              {renderAgentWarrantyTag(icon, label as string)}
            </div>
          );
        }

        return null;
      })}
      {insideBadges.slice(0, hasAgentWarranty ? 1 : 2).map((badge) => (
        <StyledFeatureFlags v2={v2} key={badge.id}>
          <div className="agent-warranty">
            {badge.icon && <img src={badge.icon} alt="label" />}
            {badge?.title?.[lang] || badge?.title?.['en'] || badge?.title?.[Object.keys(badge?.title)[0]]}
          </div>
        </StyledFeatureFlags>
      ))}
      {unShowedTags > 0 && (
        <StyledFeatureFlags v2={v2}>
          <div className="agent-warranty">+{unShowedTags}</div>
        </StyledFeatureFlags>
      )}
    </StyledFeatureBadgeWrapper>
  );
};

export default FeatureBadge;
